// roles — what a person's ROLE is called, in words, declared once.
//
// ── Why this file exists ────────────────────────────────────────────────────
//
// One person had three names. Measured in a browser at 390px on 2026-09-10,
// against the same seeded database, signed in as the same admin:
//
//   /users      Pill read "Crew"        "Manager"   "Accounting"
//   /crew       Pill read "worker"      "manager"   "accounting"
//   /security   Pill read "worker"      "manager"   "accounting"
//
// The same `Pill` component, on the same phone, two taps apart. A person reads
// "worker" on one screen and "Crew" on the next and has to work out for
// themselves that those are one thing. `worker` is not a word this company
// uses — the crew are the crew — and it is a DATABASE KEY that leaked onto a
// screen, which is the same class of defect as jobFields.js's `statusLabel`
// printing "BLOCKED" in a status pill.
//
// The cause was three tables and no contract: office/admin/Users.js kept its
// own `ROLES` list with labels, apps/shell/Me.js kept a second `ROLE_LABEL`
// that happened to agree, and Crew and Security kept none at all and printed
// `w.role` straight out of the JSON.
//
// ── The contract, and what checks it ────────────────────────────────────────
//
// This is the shape CLAUDE.md asks for — "Declare a contract once, and check
// it against the server" — and the thing it is checked against is
// server/database.js's `systemRoles` array, which is the authority on which
// roles exist at all:
//
//   const systemRoles = [
//     { name: 'admin',      permissions: adminPermissions      },
//     { name: 'manager',    permissions: managerPermissions    },
//     { name: 'accounting', permissions: accountingPermissions },
//     { name: 'worker',     permissions: workerPermissions     }
//   ];
//
// roles.test.js re-reads that file and fails if a seeded role has no label
// here — in either direction. That matters because the failure mode of a
// lookup table is silence: `ROLE_LABEL[role] || role` renders the raw key and
// looks like a label, so a fifth role added to the server would ship as
// "supervisor" in a pill and nobody would see a test go red. `roleLabel()`
// below humanises instead of falling through, and the test is what keeps the
// table complete rather than merely tidy.
//
// ── This is a LABEL, never a DECISION ───────────────────────────────────────
//
// Nothing here gates anything. Whether a person may do something is
// server/utils/permissions.js's answer, reached through `useCan(resource,
// action)` on the client — never through a role name, and
// serverRoleCheckRatchet.test.js fails the build on the server side of that
// mistake. A table keyed by role that only produces WORDS cannot become a
// permission check by accident, and it must not be extended into one.

import { ROLES } from './navigation';

/** Every role the server seeds, in the order navigation.js lists them. */
export { ROLES };

/**
 * Role key → the word a person reads.
 *
 * `worker` is "Crew" because that is what this company calls them, on the
 * screen that is itself called Crew. The other three are their own names.
 */
export const ROLE_LABELS = {
  worker: 'Crew',
  manager: 'Manager',
  accounting: 'Accounting',
  admin: 'Admin',
};

/**
 * A role as a person should read it.
 *
 * `users.role` is a plain TEXT column with no check constraint
 * (server/database.js: `role TEXT NOT NULL DEFAULT 'worker'`), and
 * `getUserPermissions` LEFT JOINs `roles ON r.name = u.role` — so an admin who
 * creates a custom role and assigns it puts a value here that this table has
 * never heard of. Printing it raw is what this file exists to stop, so an
 * unknown key is humanised the way jobFields.js's `statusLabel` humanises an
 * unknown status: separators to spaces, first letter up.
 *
 * An empty or missing role returns '' rather than inventing one. A blank cell
 * is honest; guessing "Crew" for a row whose role failed to load would state
 * something about a person's access that nothing measured.
 */
export function roleLabel(role) {
  if (role === null || role === undefined || role === '') return '';
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const words = String(role).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The `{ value, label }` pairs a <select> of roles is built from.
 *
 * office/admin/Users.js held this list itself and was the only screen with
 * labels at all. It is here so the dropdown a person picks a role from and the
 * pill they read it back in can never disagree — which is the narrower version
 * of the same bug, and the one that would be hardest to notice.
 */
export const ROLE_OPTIONS = ROLES.map((value) => ({ value, label: ROLE_LABELS[value] }));
